import {
    IProtocol,
    IProtocolStep,
    TProtocolId,
    TProtocolStepId,
} from '@/protocols/types/protocol'
import { DeepPartial } from '@/utils/types/DeepPartial'
import { IProtocolStepEvent, TProtocolStepEventId } from '@/protocols/types/IProtocolStepEvent'
import { useProtocolStore } from '@/protocols/store/protocols'
import { TCompanyId } from '@/common/types'
import { showErrorNotification } from '@/common/notifications'
import { useWriteStatusStore } from './useWriteStatusStore'
import {getWriteStore} from './writeStore'
import {deepMerge} from "@/utils/merging.ts";
import type { EnqueueArgs } from './writeStore'
import type { WriteType } from './types'

// Enqueue is the durable step of a write, and it runs BEFORE the
// React stores are touched. If the durable store rejects, nothing in
// the UI changed, so there is nothing to roll back — the failure path
// is just an error notification. Retrying transient failures is safe:
// same coalesce_key rows collapse via the store's coalesce cascade,
// distinct keys are independent.
const ENQUEUE_MAX_ATTEMPTS = 5
const ENQUEUE_RETRY_BASE_DELAY_MS = 250

let enqueueRetryBaseDelayMs = ENQUEUE_RETRY_BASE_DELAY_MS

/** Test-only hook: shrink the retry backoff so tests run instantly. */
export function __setEnqueueRetryDelayForTests(ms: number): void {
    enqueueRetryBaseDelayMs = ms
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

function errMsg(e: unknown): string {
    return e instanceof Error ? e.message : String(e)
}

/**
 * Run `enqueue` with bounded retries. `argsFactory` is re-evaluated on
 * every attempt so the payload is re-read from the current store. A
 * same-key later edit cannot land mid-retry (`commitWrite` holds the
 * per-key chain until this write finishes), but `loadProtocol` is not
 * on that chain and can replace store state while we back off. A stale
 * first-attempt snapshot would supersede the newer pending row in the
 * coalesce cascade.
 */
async function enqueueWithRetry(argsFactory: () => EnqueueArgs): Promise<number> {
    let lastError: unknown
    for (let attempt = 1; attempt <= ENQUEUE_MAX_ATTEMPTS; attempt++) {
        try {
            return await getWriteStore().enqueue(argsFactory())
        } catch (e) {
            lastError = e
            console.error(
                `[protocolWriteCoordinator] enqueue attempt ${attempt}/${ENQUEUE_MAX_ATTEMPTS} failed`, e,
            )
            if (attempt < ENQUEUE_MAX_ATTEMPTS) {
                await delay(enqueueRetryBaseDelayMs * 2 ** (attempt - 1))
            }
        }
    }
    throw lastError
}

// Burst throttle for failure toasts: Skip All on a dead store fails
// N+1 writes within seconds. Show the first failure as a toast, and
// log the rest to the console only while the toast is on screen.
// Note: mantine's notifications.update() is a no-op for ids that are
// not currently shown, so a fixed-id "update once" toast would never
// render at all — show real toasts and throttle instead.
const FAILURE_TOAST_WINDOW_MS = 10_000
let failureToastWindowMs = FAILURE_TOAST_WINDOW_MS
let lastFailureToastAt = 0

/** Test-only hook: set the failure-toast throttle window (0 = always toast). */
export function __setFailureToastWindowForTests(ms: number): void {
    failureToastWindowMs = ms
    lastFailureToastAt = 0
}

/**
 * Terminal enqueue failure. The React stores were never updated
 * (enqueue runs first), so the user's edit simply did not happen —
 * surface that.
 */
function notifyEnqueueFailure(writeType: WriteType, coalesceKey: string | null, error: unknown): void {
    console.error(
        `[protocolWriteCoordinator] ${writeType} enqueue failed after ${ENQUEUE_MAX_ATTEMPTS} attempts`,
        { coalesceKey }, error,
    )
    const now = Date.now()
    if (now - lastFailureToastAt < failureToastWindowMs) return
    lastFailureToastAt = now
    showErrorNotification({
        title: 'Change could not be saved',
        message: `${writeType} for ${coalesceKey ?? 'protocol'} was not queued: ${errMsg(error)}. The change was not applied.`,
    })
}

/**
 * Tags a terminal enqueue failure so fire-and-forget write paths can
 * swallow exactly that error without hiding post-enqueue failures
 * (store apply / reconcile), which must stay visible in the console.
 */
class EnqueueFailedError extends Error {
    readonly cause: unknown
    constructor(cause: unknown) {
        super(`enqueue failed: ${errMsg(cause)}`)
        this.name = 'EnqueueFailedError'
        this.cause = cause
    }
}

function swallowFireAndForgetError(e: unknown): void {
    if (e instanceof EnqueueFailedError) return
    // Enqueue succeeded; applying to the store or reconciling failed.
    // The row IS queued — log loudly instead of letting the divergence
    // pass silently.
    console.error('[protocolWriteCoordinator] post-enqueue processing failed', e)
}

// Per-coalesceKey serialization of the whole write flow (payload →
// enqueue → store update). Payloads are computed from the React
// stores, so a same-key write must not start until the previous one
// has landed its store update — otherwise its payload would miss the
// prior edit, and the coalesce cascade would persist the stale merge
// over the newer row. Different keys stay fully parallel.
const writeChains = new Map<string, Promise<void>>()

function serializeByKey<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const prev = writeChains.get(key) ?? Promise.resolve()
    const current = prev.then(fn, fn)
    // Chain link swallows outcomes so one write cannot wedge the rest.
    writeChains.set(key, current.then(() => undefined, () => undefined))
    return current
}

function writeChainKey(companyId: TCompanyId, protocolId: TProtocolId, coalesceKey: string): string {
    return `${companyId}:${protocolId}:${coalesceKey}`
}

// Public write* calls in flight, including those parked on
// serializeByKey. Remount (formResetEpoch) waits until this is 0 so
// a failed write cannot snapshot the React store while a sibling
// enqueue has succeeded but not yet run onSuccess.
let activeWriteCount = 0
let pendingFormReset = false

function trackWrite<T>(fn: () => Promise<T>): Promise<T> {
    activeWriteCount++
    return fn().finally(() => {
        activeWriteCount--
        if (pendingFormReset && activeWriteCount === 0) {
            pendingFormReset = false
            useWriteStatusStore.getState().bumpFormResetEpoch()
        }
    })
}

/** Test-only: drop in-flight accounting so a hung write cannot leak. */
export function __resetWriteActivityForTests(): void {
    activeWriteCount = 0
    pendingFormReset = false
}

function readStep(protocolId: TProtocolId, stepId: TProtocolStepId): IProtocolStep | undefined {
    const protocol = useProtocolStore.getState().protocols.find(p => p.id === protocolId)
    return protocol?.steps.find(s => s.id === stepId)
}

function readProtocol(protocolId: TProtocolId): IProtocol | undefined {
    return useProtocolStore.getState().protocols.find(p => p.id === protocolId)
}

// After enqueue, the Write Store cascade may have superseded a prior
// pending/dead row for this coalesce key. The per-row icon stays
// correct (markPending set it to 'pending', which still
// matches the new row), but the aggregate counts and dead-row
// list are now stale relative to WriteStore. Re-fetch to sync.
//
// Trying to mirror the cascade in JS with increment/decrement
// math misses edge cases — the prior dead row's count and
// deadWrites entry would persist past the cascade and the new
// row's failure would double-count. WriteStore is the source of truth.
async function reconcileWriteStore(companyId: TCompanyId, protocolId: TProtocolId): Promise<void> {
    const store = getWriteStore()
    const summary = await store.summary(
        companyId as unknown as string,
        protocolId as unknown as string,
    )
    const deadRows = await store.getPendingWrites({
        companyId: companyId as unknown as string,
        protocolId: protocolId as unknown as string,
        stateFilter: ['dead'],
        coalesceView: false,
    })
    useWriteStatusStore.getState().reconcileFromWriteStore(summary, deadRows)
}

/**
 * Shared write machine: serialize by coalesce key, enqueue (with retry),
 * notify+rethrow on terminal failure, then run `onSuccess` and reconcile.
 *
 * Always rejects after notifying. Fire-and-forget exports (`writeStep`,
 * `writeProtocol`) catch at their boundary; list-mutating exports
 * (`writeEvent`, `writeEventDelete`) let the rejection through so the
 * caller does not update UI for a write that was never queued.
 */
async function commitWrite<T = void>(opts: {
    companyId: TCompanyId
    protocolId: TProtocolId
    coalesceKey: string
    writeType: WriteType
    argsFactory: () => EnqueueArgs
    onStart?: () => void
    onEnqueueFailed?: () => void
    onSuccess?: () => T
}): Promise<T> {
    return serializeByKey(writeChainKey(opts.companyId, opts.protocolId, opts.coalesceKey), async () => {
        opts.onStart?.()
        try {
            await enqueueWithRetry(opts.argsFactory)
        } catch (e) {
            opts.onEnqueueFailed?.()
            pendingFormReset = true
            notifyEnqueueFailure(opts.writeType, opts.coalesceKey, e)
            throw new EnqueueFailedError(e)
        }
        const result = opts.onSuccess ? opts.onSuccess() : (undefined as T)
        await reconcileWriteStore(opts.companyId, opts.protocolId)
        return result
    })
}

/**
 * Enqueue a step_update, then apply it to the React stores.
 * Swallows terminal enqueue failure after notifying — fire-and-forget
 * callers (`void writeStep(...)`) must not see an unhandled rejection.
 */
export async function writeStep(
    companyId: TCompanyId,
    protocolId: TProtocolId,
    stepId: TProtocolStepId,
    updates: DeepPartial<IProtocolStep>,
): Promise<void> {
    const base = readStep(protocolId, stepId)
    if (!base) return

    await trackWrite(async () => {
        try {
            await commitWrite({
            companyId,
            protocolId,
            coalesceKey: stepId as unknown as string,
            writeType: 'step_update',
            // Icon-only, before enqueue, inside the per-key chain: the
            // row does not exist yet, so the drainer cannot persist it
            // first. markPending *after* enqueue is the race (INSERT is
            // visible, claim→server→markPersisted can finish, then a
            // late pending never clears — reconcile does not touch
            // perWrite). Undo on enqueue failure before the next
            // same-key write starts.
            onStart: () => useWriteStatusStore.getState().markPending(stepId),
            onEnqueueFailed: () => useWriteStatusStore.getState().clearPending(stepId),
            argsFactory: () => {
                // Re-read on every attempt: loadProtocol can replace
                // store state while we back off (same-key edits cannot —
                // commitWrite holds the chain).
                const next = deepMerge(readStep(protocolId, stepId) ?? base, updates)
                return {
                    companyId,
                    protocolId: protocolId as unknown as string,
                    coalesceKey: stepId as unknown as string,
                    stepId: stepId as unknown as string,
                    writeType: 'step_update',
                    payload: next as unknown as Record<string, unknown>,
                    changes: updates as unknown as Record<string, unknown>,
                }
            },
            onSuccess: () => {
                // Enqueue succeeded — now apply the edit. Guard the
                // re-read: if the step vanished mid-flight, the queued
                // row is merged back on the next loadProtocol.
                if (readStep(protocolId, stepId)) {
                    useProtocolStore.getState().updateStep(companyId, protocolId, stepId, updates)
                }
            },
            })
        } catch (e) {
            swallowFireAndForgetError(e)
        }
    })
}

/**
 * Enqueue a protocol_update, then apply it to the React stores.
 * Swallows terminal enqueue failure after notifying — fire-and-forget
 * callers (`void writeProtocol(...)`) must not see an unhandled rejection.
 */
export async function writeProtocol(
    companyId: TCompanyId,
    protocolId: TProtocolId,
    updates: DeepPartial<IProtocol>,
): Promise<void> {
    const baseProtocol = readProtocol(protocolId)
    if (!baseProtocol) return

    await trackWrite(async () => {
        try {
            await commitWrite({
            companyId,
            protocolId,
            coalesceKey: protocolId as unknown as string,
            writeType: 'protocol_update',
            argsFactory: () => {
                // Strip 'steps' from the payload: the server ignores steps on
                // protocol_update, so sending them is wasted bandwidth AND
                // introduces a bug on resume (the JSON round-trip through SQLite
                // stringifies dayjs dates; on reload the spread in loadProtocol
                // would overwrite the store's dayjs steps with string-dated ones
                // from the payload, crashing ZoneAwareDateTimePicker).
                const merged = deepMerge(readProtocol(protocolId) ?? baseProtocol, updates)
                const { steps: _steps, ...protocolWithoutSteps } = merged
                return {
                    companyId: companyId as unknown as string,
                    protocolId: protocolId as unknown as string,
                    coalesceKey: protocolId as unknown as string,
                    stepId: null,
                    writeType: 'protocol_update',
                    payload: protocolWithoutSteps as unknown as Record<string, unknown>,
                    changes: updates as unknown as Record<string, unknown>,
                }
            },
            onSuccess: () => {
                // Recompute the merge against the CURRENT store so step
                // updates that landed while the enqueue was in flight
                // are not clobbered by this write.
                const current = readProtocol(protocolId)
                if (current) {
                    useProtocolStore.getState().updateProtocol(companyId, deepMerge(current, updates))
                }
            },
            })
        } catch (e) {
            swallowFireAndForgetError(e)
        }
    })
}

/**
 * Enqueue an event_create and return the event for the caller to add
 * to its list. Rejects on terminal enqueue failure (after notifying)
 * so the caller does not render an event that was never queued.
 *
 * The canonical client-side UUID is generated upstream (e.g., by
 * EventRecorder) and passed in as `eventData.id`. It is the same
 * id used throughout the event's lifetime: local UI, coalesceKey on
 * enqueue, status-store key, delete URL. The server accepts it via
 * the optional `id` field on CreateEventRequest (see the backend
 * route), so the same id flows through create + delete with no
 * tempId → realId translation. Inner `IEvent.id` is also generated
 * so `onDeleteEvent`'s `e.event.id === eventId` lookup stays unique
 * even with multiple pending local events.
 */
export async function writeEvent(
    companyId: TCompanyId,
    protocolId: TProtocolId,
    eventData: IProtocolStepEvent,
): Promise<IProtocolStepEvent> {
    const eventId = eventData.id as unknown as string
    const queuedEvent: IProtocolStepEvent = {
        ...eventData,
        protocolId: protocolId as unknown as TProtocolId,
        event: eventData.event
    }

    return trackWrite(() => commitWrite({
        companyId,
        protocolId,
        coalesceKey: eventId,
        writeType: 'event_create',
        argsFactory: () => ({
            companyId: companyId as unknown as string,
            protocolId: protocolId as unknown as string,
            coalesceKey: eventId,
            stepId: null,
            writeType: 'event_create',
            payload: queuedEvent as unknown as Record<string, unknown>,
        }),
        onSuccess: () => queuedEvent,
    }))
}

/**
 * Enqueue an event_delete. Rejects on terminal enqueue failure (after
 * notifying) so the caller keeps the event visible — nothing was
 * queued, nothing should be removed.
 *
 * The eventId is the canonical id used at create time (either the
 * client's UUID for locally-created events, or the server's id for
 * already-persisted events).
 *
 * The delete is always enqueued normally — no supersede cascade
 * against a prior pending event_create. If the create is still
 * in-flight, the drainer dispatches both: server creates then deletes
 * (eventually consistent, no data leak — see the in-flight race that
 * motivated dropping the earlier Case A cascade). The per-eventId
 * serialization above also guarantees the create's enqueue settles
 * before the delete's starts, preserving that dispatch order.
 *
 * If the create failed permanently or never reached the server, the
 * delete is dispatched, the server returns 404, and the delete
 * becomes a dead row the user can retry or discard from the
 * PendingWritesTable.
 */
export async function writeEventDelete(
    companyId: TCompanyId,
    protocolId: TProtocolId,
    eventId: TProtocolStepEventId,
): Promise<void> {
    const writeKey = eventId as unknown as string

    await trackWrite(() => commitWrite({
        companyId,
        protocolId,
        coalesceKey: writeKey,
        writeType: 'event_delete',
        argsFactory: () => ({
            companyId: companyId as unknown as string,
            protocolId: protocolId as unknown as string,
            coalesceKey: writeKey,
            stepId: null,
            writeType: 'event_delete',
            payload: {id: writeKey} as unknown as Record<string, unknown>,
        }),
    }))
}
